'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { getIsOpenDrawerRequested } from 'DesignData/selectors/statusBar';
import { mapStateToProps as mapEditorErrorsStateToProps } from 'DesignComponents/statusBar/connect/editorErrors';
import UIBox from 'UIComponents/layout/UIBox';
import UIFlex from 'UIComponents/layout/UIFlex';
import StatusBarDrawer from 'DesignComponents/statusBar/drawer/StatusBarDrawer';
import StatusBarStatus from 'DesignComponents/statusBar/StatusBarStatus';
import StatusBarSettings from 'DesignComponents/statusBar/StatusBarSettings';
import StatusBarFilePath from 'DesignComponents/statusBar/StatusBarFilePath';
import StatusBarReference from 'DesignComponents/statusBar/StatusBarReference';
import StatusBarLanguage from 'DesignComponents/statusBar/StatusBarLanguage';
import StatusBarFileType from 'DesignComponents/statusBar/StatusBarFileType';
import StatusBarFileSize from 'DesignComponents/statusBar/StatusBarFileSize';
import StatusBarChangelog from 'DesignComponents/statusBar/StatusBarChangelog';
import StatusBarGettingStarted from 'DesignComponents/statusBar/StatusBarGettingStarted';
import StatusBarFileDimensions from 'DesignComponents/statusBar/StatusBarFileDimensions';
import StatusBarProjects from 'DesignComponents/statusBar/StatusBarProjects';
import CodemirrorType from 'DesignComponents/statusBar/CodemirrorType';
import CodemirrorSelections from 'DesignComponents/statusBar/CodemirrorSelections';
const StyledHeaderBox = styled(UIBox).withConfig({
  displayName: "StatusBar__StyledHeaderBox",
  componentId: "sc-1va7tee-0"
})(["display:flex;align-items:center;"]);

const StatusBar = ({
  hasDrawerContents,
  isOpenDrawerRequested
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const determineDrawerState = () => {
    if (isDrawerOpen && !hasDrawerContents) {
      return false;
    } else if (!isDrawerOpen && hasDrawerContents && isOpenDrawerRequested) {
      return true;
    }

    return isDrawerOpen;
  };

  useEffect(() => {
    setIsDrawerOpen(determineDrawerState()); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const _isDrawerOpen = determineDrawerState();

    if (_isDrawerOpen !== isDrawerOpen) {
      setIsDrawerOpen(_isDrawerOpen);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [isDrawerOpen, isOpenDrawerRequested]);

  const toggleDrawer = _isDrawerOpen => {
    const value = hasDrawerContents && (_isDrawerOpen === !!_isDrawerOpen ? _isDrawerOpen : !isDrawerOpen);
    setIsDrawerOpen(value);
  };

  const renderReference = () => {
    return /*#__PURE__*/_jsx(StatusBarReference, {}, "status-bar-hubl-docs");
  };

  const renderFilePath = () => {
    return /*#__PURE__*/_jsx(StatusBarFilePath, {}, "status-bar-file-path");
  };

  const renderSettingsControls = () => {
    return /*#__PURE__*/_jsx(StatusBarSettings, {}, "status-bar-settings");
  };

  const renderCodemirrorType = () => {
    return /*#__PURE__*/_jsx(CodemirrorType, {}, "status-bar-codemirror-type");
  };

  const renderCodemirrorSelections = () => {
    return /*#__PURE__*/_jsx(CodemirrorSelections, {}, "status-bar-codemirror-selections");
  };

  const renderLanguage = () => {
    return /*#__PURE__*/_jsx(StatusBarLanguage, {}, "status-bar-language");
  };

  const renderFileType = () => {
    return /*#__PURE__*/_jsx(StatusBarFileType, {}, "status-bar-file-type");
  };

  const renderFileSize = () => {
    return /*#__PURE__*/_jsx(StatusBarFileSize, {}, "status-bar-file-size");
  };

  const renderFileDimensions = () => {
    return /*#__PURE__*/_jsx(StatusBarFileDimensions, {}, "status-bar-file-dimensions");
  };

  const renderProjects = () => {
    return /*#__PURE__*/_jsx(StatusBarProjects, {}, "status-bar-projects");
  };

  const renderStatus = () => {
    return /*#__PURE__*/_jsx(StatusBarStatus, {
      toggleDrawer: toggleDrawer,
      isDrawerOpen: isDrawerOpen
    }, "status-bar-status");
  };

  const renderGettingStarted = () => /*#__PURE__*/_jsx(StatusBarGettingStarted, {}, "status-bar-getting-started");

  const renderChangelog = () => /*#__PURE__*/_jsx(StatusBarChangelog, {}, "status-bar-changelog");

  const renderStatusBar = () => {
    const leftContent = [renderStatus(), renderFileType(), renderFilePath(), renderLanguage(), renderFileSize(), renderFileDimensions()];
    const middleContent = [];
    const rightContent = [renderCodemirrorType(), renderCodemirrorSelections(), renderGettingStarted(), renderProjects(), renderChangelog(), renderReference(), renderSettingsControls()];
    return /*#__PURE__*/_jsxs(UIFlex, {
      align: "stretch",
      wrap: "nowrap",
      children: [/*#__PURE__*/_jsx(StyledHeaderBox, {
        grow: 0,
        shrink: 0,
        children: leftContent
      }), /*#__PURE__*/_jsx(StyledHeaderBox, {
        grow: 1,
        shrink: 1,
        children: /*#__PURE__*/_jsx("span", {
          className: "is--text--help",
          children: middleContent
        })
      }), /*#__PURE__*/_jsx(StyledHeaderBox, {
        grow: 0,
        shrink: 0,
        children: rightContent
      })]
    });
  };

  const detailsClasses = 'status-bar-drawer-container' + (!isDrawerOpen ? " is--hidden" : "");
  return /*#__PURE__*/_jsxs("div", {
    className: "status-bar",
    children: [/*#__PURE__*/_jsx("div", {
      className: detailsClasses,
      children: /*#__PURE__*/_jsx("div", {
        className: "status-bar-details",
        children: /*#__PURE__*/_jsx(StatusBarDrawer, {
          isDrawerOpen: isDrawerOpen
        })
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "status-bar-header",
      children: renderStatusBar()
    })]
  });
};

StatusBar.propTypes = {
  hasDrawerContents: PropTypes.bool.isRequired,
  isOpenDrawerRequested: PropTypes.bool
};
export default connect(state => ({
  hasDrawerContents: mapEditorErrorsStateToProps(state).hasMessages,
  isOpenDrawerRequested: getIsOpenDrawerRequested(state)
}), null)(StatusBar);